import { useEffect, useRef } from 'react';
import { useMap } from 'react-leaflet';
import L from 'leaflet';
import { useDispatch, useSelector } from 'react-redux';
import { debounce } from '@mui/material/utils';
import { setMapBounds, setSelected } from '../../../modules/conteneurMap/conteneurMap.slice';
import { getStatusColor, getChecklistColor, getChecklistWeight, CHECKLIST_ETAT } from '../../../modules/conteneurMap/conteneurMap.helpers';

/**
 * Layer Leaflet custom dessinant les conteneurs sous forme de petits carrés
 * arrondis (primitives Canvas natives), coloriés selon `statutWorkflow`.
 *
 * - Utilise le renderer Canvas natif Leaflet (`L.canvas`) et dessine dans son
 *   contexte récupéré via `this._renderer._ctx`.
 * - `_redraw` câblé sur `moveend` / `zoomend` / `resize`.
 * - Hit-testing : recherche du feature le plus proche via `Math.hypot`,
 *   tolérance 8px, dispatch `setSelected(id)`.
 * - PAT-04 : en mode checklist la couleur vient de `properties.checklist` (R4/R5) et une
 *   heatmap optionnelle (R8, poids = nb d'éléments en défaut) est dessinée sous les marqueurs,
 *   dans le même canvas — pas de dépendance supplémentaire.
 */

const HIT_RADIUS = 8;

/**
 * Dessine un marqueur "carré arrondi" centré sur (x, y).
 * `roundRect` est une primitive Canvas native (Chromium / FF récents).
 * Une fine bordure sombre semi-transparente améliore la lisibilité dézoomé.
 */
function drawMarker(ctx, x, y, color, s = 5, hollow = false) {
    ctx.beginPath();
    ctx.roundRect(x - s, y - s, s * 2, s * 2, 2);
    if (hollow) {
        // Gris « nodata » : carré creux, discret — il ne doit pas concurrencer l'information.
        ctx.fillStyle = 'rgba(255, 255, 255, 0.55)';
        ctx.fill();
        ctx.lineWidth = 1;
        ctx.strokeStyle = color;
        ctx.stroke();
        return;
    }
    ctx.fillStyle = color;
    ctx.fill();
    ctx.lineWidth = s >= 6 ? 1.25 : 0.75;
    ctx.strokeStyle = s >= 6 ? 'rgba(255, 255, 255, 0.95)' : 'rgba(0, 0, 0, 0.45)';
    ctx.stroke();
}

/**
 * Hiérarchie visuelle en mode checklist (PAT-04) : rang de dessin (plus grand = dessiné au-dessus),
 * taille et style par état. Les problèmes et tentatives passent au-dessus des RAS, eux-mêmes au-dessus
 * du gris nodata. En mode lavage : rang unique, style historique.
 */
function styleFor(checklistMode, checklist, status) {
    if (!checklistMode) return { rank: 1, size: 5, hollow: false, color: getStatusColor(status) };
    const etat = checklist?.etat ?? CHECKLIST_ETAT.NON_INSPECTE;
    const color = getChecklistColor(checklist);
    if (etat === CHECKLIST_ETAT.PROBLEME) return { rank: 3, size: 7, hollow: false, color };
    if (etat === CHECKLIST_ETAT.TENTATIVE_IMPOSSIBLE) return { rank: 3, size: 7, hollow: false, color };
    if (etat === CHECKLIST_ETAT.RAS) return { rank: 2, size: 5, hollow: false, color };
    return { rank: 0, size: 4, hollow: true, color };
}

/**
 * Heatmap R8 par accumulation (principe simpleheat / leaflet.heat, sans dépendance) :
 * 1. chaque point pondéré dépose un disque radial noir d'alpha ∝ poids dans un canvas hors écran ;
 *    les dépôts s'additionnent là où les points se concentrent ;
 * 2. l'alpha accumulé est colorisé par un gradient (jaune → orange → rouge) et plaqué sur le canvas carte.
 * Le résultat est une densité : une zone à 5 AB en défaut rougit, un AB isolé reste un halo pâle.
 */
const HEAT_RADIUS = 26;
const HEAT_BLUR = 18;
let heatGradientCache = null;
function heatGradient() {
    if (heatGradientCache) return heatGradientCache;
    const c = document.createElement('canvas');
    c.width = 1; c.height = 256;
    const g = c.getContext('2d');
    const grad = g.createLinearGradient(0, 0, 0, 256);
    grad.addColorStop(0.0, 'rgba(255, 230, 120, 0)');
    grad.addColorStop(0.25, 'rgba(255, 210, 90, 0.55)');
    grad.addColorStop(0.55, 'rgba(243, 154, 63, 0.8)');
    grad.addColorStop(0.85, 'rgba(216, 60, 20, 0.9)');
    grad.addColorStop(1.0, 'rgba(160, 20, 20, 0.95)');
    g.fillStyle = grad;
    g.fillRect(0, 0, 1, 256);
    heatGradientCache = g.getImageData(0, 0, 1, 256).data;
    return heatGradientCache;
}
function drawHeat(ctx, drawables, maxWeight) {
    const weighted = drawables.filter((d) => d.active && d.weight > 0);
    if (weighted.length === 0) return;
    const { width, height } = ctx.canvas;
    const off = document.createElement('canvas');
    off.width = width; off.height = height;
    const o = off.getContext('2d');
    const r = HEAT_RADIUS + HEAT_BLUR;
    weighted.forEach((d) => {
        if (d.pt.x < -r || d.pt.y < -r || d.pt.x > width + r || d.pt.y > height + r) return;
        const a = Math.min(1, 0.35 + 0.65 * (d.weight / maxWeight));
        const g = o.createRadialGradient(d.pt.x, d.pt.y, 0, d.pt.x, d.pt.y, r);
        g.addColorStop(0, `rgba(0,0,0,${a})`);
        g.addColorStop(HEAT_RADIUS / r, `rgba(0,0,0,${a * 0.45})`);
        g.addColorStop(1, 'rgba(0,0,0,0)');
        o.fillStyle = g;
        o.beginPath();
        o.arc(d.pt.x, d.pt.y, r, 0, Math.PI * 2);
        o.fill();
    });
    const img = o.getImageData(0, 0, width, height);
    const px = img.data;
    const grad = heatGradient();
    for (let i = 0; i < px.length; i += 4) {
        const a = px[i + 3];
        if (a === 0) continue;
        const j = a * 4; // alpha 0..255 → ligne du gradient
        px[i] = grad[j]; px[i + 1] = grad[j + 1]; px[i + 2] = grad[j + 2];
        px[i + 3] = grad[j + 3];
    }
    o.putImageData(img, 0, 0);
    ctx.drawImage(off, 0, 0);
}

const ConteneurBornesLayer = L.Layer.extend({
    initialize(options) {
        L.setOptions(this, options);
        this._features = [];
        this._selectedId = null;
        this._activeIds = null; // null = aucun filtre, tout afficher normalement
        this._checklistMode = false;
        this._heatmap = false;
        this._ghostInactive = true;
    },

    /** PAT-04 : bascule couleur lavage ↔ checklist, heatmap, et fantômes (false = inactifs masqués). */
    setDisplayMode(checklistMode, heatmap, ghostInactive = true) {
        this._checklistMode = !!checklistMode;
        this._heatmap = !!heatmap;
        this._ghostInactive = !!ghostInactive;
        this._redraw();
    },

    setFeatures(features) {
        this._features = Array.isArray(features) ? features : [];
        this._redraw();
    },

    setSelectedId(id) {
        this._selectedId = id;
        this._redraw();
    },

    /**
     * Définit l'ensemble des ids "actifs" (matchant les filtres).
     * `null` → pas de filtre, tout est affiché normalement.
     */
    setActiveIds(set) {
        // Accepte tout objet "set-like" exposant has() (robuste cross-realm).
        if (set && typeof set.has === 'function') {
            this._activeIds = set;
        } else {
            this._activeIds = null;
        }
        this._redraw();
    },

    onAdd(map) {
        this._map = map;

        // Renderer Canvas natif Leaflet partagé : on dessine dans son _ctx.
        this._renderer = L.canvas({ padding: 0.5 });
        this._renderer.addTo(map);

        map.on('moveend', this._redraw, this);
        map.on('zoomend', this._redraw, this);
        map.on('resize', this._redraw, this);

        this._redraw();
    },

    onRemove(map) {
        map.off('moveend', this._redraw, this);
        map.off('zoomend', this._redraw, this);
        map.off('resize', this._redraw, this);
        if (this._renderer) {
            map.removeLayer(this._renderer);
            this._renderer = null;
        }
        this._map = null;
    },

    _redraw() {
        if (!this._map || !this._renderer || !this._renderer._ctx) return;

        const ctx = this._renderer._ctx;
        const map = this._map;

        // Le renderer Leaflet gère le clearRect lui-même au moveend, mais on
        // force le nettoyage de notre couche pour repartir propre.
        ctx.clearRect(0, 0, ctx.canvas.width, ctx.canvas.height);

        const activeIds = this._activeIds; // Set ou null
        const GHOST_COLOR = '#9e9e9e';

        // Pré-calcul des points (lat/lng → container) une seule fois.
        const drawables = [];
        this._features.forEach((feature) => {
            const props = feature?.properties || {};
            const coords = feature?.geometry?.coordinates || [];
            const lng = props.longitude ?? coords[0];
            const lat = props.latitude ?? coords[1];
            if (lat == null || lng == null) return;
            const fLat = typeof lat === 'number' ? lat : parseFloat(lat);
            const fLng = typeof lng === 'number' ? lng : parseFloat(lng);
            if (Number.isNaN(fLat) || Number.isNaN(fLng)) return;
            const pt = map.latLngToLayerPoint([fLat, fLng]);
            const id = props.id;
            const active = activeIds == null ? true : (id != null && activeIds.has(id));
            if (!active && !this._ghostInactive) return; // masqué, pas de fantôme (mode checklist)
            const style = styleFor(this._checklistMode, props.checklist, props.status);
            const weight = this._checklistMode ? getChecklistWeight(props.checklist) : 0;
            drawables.push({ pt, id, weight, active, ...style });
        });

        // Ordre de dessin : rang croissant (gris nodata dessous, problèmes au-dessus).
        drawables.sort((a, b) => a.rank - b.rank);

        // Passe 0 : heatmap (mode checklist uniquement), sous les marqueurs.
        if (this._checklistMode && this._heatmap) {
            const maxWeight = drawables.reduce((m, d) => (d.active && d.weight > m ? d.weight : m), 1);
            drawHeat(ctx, drawables, maxWeight);
        }

        // Passe 1 : fantômes (hors Set) — gris translucide (mode lavage).
        if (activeIds != null && this._ghostInactive) {
            drawables.forEach((d) => {
                if (d.active) return;
                ctx.save();
                ctx.globalAlpha = 0.5;
                drawMarker(ctx, d.pt.x, d.pt.y, GHOST_COLOR);
                ctx.globalAlpha = 1;
                ctx.restore();
            });
        }

        // Passe 2 : actifs (ou tous si aucun filtre) — alpha plein, couleur statut + dot blanc.
        drawables.forEach((d) => {
            if (!d.active) return;
            const isSelected = d.id != null && d.id === this._selectedId;
            ctx.save();
            // Heatmap active : les marqueurs sans poids s'effacent derrière la densité.
            ctx.globalAlpha = (this._checklistMode && this._heatmap && d.weight === 0) ? 0.45 : 1;
            drawMarker(ctx, d.pt.x, d.pt.y, d.color, d.size, d.hollow);
            if (!this._checklistMode) {
                // Dot blanc central : style historique du mode lavage
                ctx.beginPath();
                ctx.arc(d.pt.x, d.pt.y, 1.8, 0, Math.PI * 2);
                ctx.fillStyle = 'rgba(255, 255, 255, 0.85)';
                ctx.fill();
            }
            if (isSelected) {
                ctx.globalAlpha = 1;
                ctx.beginPath();
                ctx.arc(d.pt.x, d.pt.y, d.size + 4, 0, Math.PI * 2);
                ctx.strokeStyle = 'rgba(33, 48, 103, 0.9)';
                ctx.lineWidth = 2;
                ctx.stroke();
            }
            ctx.globalAlpha = 1;
            ctx.restore();
        });
    },

    /**
     * Hit-test : retourne l'id du feature le plus proche dans `HIT_RADIUS` px,
     * ou null si rien n'est trouvé.
     * `layerPoint` = `e.layerPoint` du clic Leaflet : même repère que `latLngToLayerPoint`
     * utilisé au dessin (le repère conteneur dérive dès que la carte a été déplacée).
     */
    findFeatureAt(layerPoint) {
        if (!this._map) return null;
        const map = this._map;
        let bestId = null;
        let bestDist = HIT_RADIUS;

        this._features.forEach((feature) => {
            const props = feature?.properties || {};
            const coords = feature?.geometry?.coordinates || [];
            const lng = props.longitude ?? coords[0];
            const lat = props.latitude ?? coords[1];
            if (lat == null || lng == null || props.id == null) return;
            // Masqué (mode checklist, hors filtre) : pas cliquable.
            if (!this._ghostInactive && this._activeIds != null && !this._activeIds.has(props.id)) return;

            const fLat = typeof lat === 'number' ? lat : parseFloat(lat);
            const fLng = typeof lng === 'number' ? lng : parseFloat(lng);
            if (Number.isNaN(fLat) || Number.isNaN(fLng)) return;

            const pt = map.latLngToLayerPoint([fLat, fLng]);
            const dist = Math.hypot(pt.x - layerPoint.x, pt.y - layerPoint.y);
            if (dist <= bestDist) {
                bestDist = dist;
                bestId = props.id;
            }
        });

        return bestId;
    },
});

/**
 * Wrapper React qui instancie le layer une seule fois, et le synchronise avec
 * les props (`features`) et le store (`selectedId`).
 */
function ConteneurCanvasLayer({ features, activeIds, checklistMode = false, heatmap = false, ghostInactive = true }) {
    const map = useMap();
    const dispatch = useDispatch();
    const selectedId = useSelector((s) => s.conteneurMap.selectedId);

    const layerRef = useRef(null);
    const didFitRef = useRef(false);

    // Création / destruction du layer
    useEffect(() => {
        if (!map) return undefined;

        const layer = new ConteneurBornesLayer();
        layerRef.current = layer;
        layer.addTo(map);

        // Hit-test sur clic map
        const onMapClick = (e) => {
            const id = layer.findFeatureAt(e.layerPoint);
            if (id != null) {
                dispatch(setSelected(id));
            }
        };
        map.on('click', onMapClick);

        return () => {
            map.off('click', onMapClick);
            map.removeLayer(layer);
            layerRef.current = null;
        };
    }, [map, dispatch]);

    // Sync features
    useEffect(() => {
        const layer = layerRef.current;
        if (!layer) return;
        layer.setFeatures(features || []);

        if (!didFitRef.current && Array.isArray(features) && features.length > 0) {
            const latLngs = [];
            features.forEach((f) => {
                const props = f?.properties || {};
                const coords = f?.geometry?.coordinates || [];
                const lng = props.longitude ?? coords[0];
                const lat = props.latitude ?? coords[1];
                if (lat == null || lng == null) return;
                const fLat = typeof lat === 'number' ? lat : parseFloat(lat);
                const fLng = typeof lng === 'number' ? lng : parseFloat(lng);
                if (Number.isNaN(fLat) || Number.isNaN(fLng)) return;
                latLngs.push([fLat, fLng]);
            });
            if (latLngs.length > 0) {
                try {
                    map.fitBounds(L.latLngBounds(latLngs), { padding: [20, 20], maxZoom: 16 });
                    didFitRef.current = true;
                } catch (e) {
                    // ignore
                }
            }
        }
    }, [features, map]);

    // Sync activeIds (Set ou null) — ne recrée pas le layer
    useEffect(() => {
        const layer = layerRef.current;
        if (!layer) return;
        layer.setActiveIds(activeIds);
    }, [activeIds]);

    // Sync mode d'affichage (couleur checklist, heatmap) — ne recrée pas le layer
    useEffect(() => {
        const layer = layerRef.current;
        if (!layer) return;
        layer.setDisplayMode(checklistMode, heatmap, ghostInactive);
    }, [checklistMode, heatmap, ghostInactive]);

    // Sync selected
    useEffect(() => {
        const layer = layerRef.current;
        if (!layer) return;
        layer.setSelectedId(selectedId);
        if (selectedId != null) {
            const f = (features || []).find((x) => x?.properties?.id === selectedId);
            if (f) {
                const props = f.properties || {};
                const coords = f.geometry?.coordinates || [];
                const lng = props.longitude ?? coords[0];
                const lat = props.latitude ?? coords[1];
                const fLat = typeof lat === 'number' ? lat : parseFloat(lat);
                const fLng = typeof lng === 'number' ? lng : parseFloat(lng);
                if (!Number.isNaN(fLat) && !Number.isNaN(fLng)) {
                    map.panTo([fLat, fLng], { animate: true });
                }
            }
        }
    }, [selectedId, features, map]);

    // Publication de la bbox au moveend (debounce 200ms)
    useEffect(() => {
        if (!map) return undefined;

        const publishBounds = () => {
            const b = map.getBounds();
            dispatch(setMapBounds({
                north: b.getNorth(),
                south: b.getSouth(),
                east: b.getEast(),
                west: b.getWest(),
            }));
        };
        const debounced = debounce(publishBounds, 200);

        publishBounds();
        map.on('moveend', debounced);
        map.on('zoomend', debounced);

        return () => {
            map.off('moveend', debounced);
            map.off('zoomend', debounced);
        };
    }, [map, dispatch]);

    return null;
}

export default ConteneurCanvasLayer;
